/**
 * Owner — จัดการ "เจ้าของระบบ (Owner)" (OWNER เท่านั้น)
 * ดูรายชื่อเจ้าของปัจจุบัน · ตั้งพนักงานเป็นเจ้าของเพิ่ม (ได้หลายคน) · ถอดเจ้าของ
 * (ถอดคนสุดท้ายไม่ได้ — backend กันไว้). HR เข้าหน้านี้ไม่ได้.
 */
import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Loading } from '../../components/layout/Loading';
import { useOwners, useSetEmployeeRole } from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { useAuthStore } from '../../lib/store';
import { Crown, ShieldOff, UserPlus } from 'lucide-react';

export function OwnerOwners() {
  const myRole = useAuthStore((s) => s.profile?.role);
  const isOwner = myRole === 'OWNER';
  const { data, isLoading } = useOwners(isOwner);
  const setRole = useSetEmployeeRole();
  const toast = useToast();
  const [pick, setPick] = useState('');

  if (!isOwner) {
    return (
      <AdminLayout title="เจ้าของระบบ">
        <div className="card p-4 text-sm text-gray-600">หน้านี้เฉพาะเจ้าของระบบ (Owner) เท่านั้น</div>
      </AdminLayout>
    );
  }
  if (isLoading) return <AdminLayout title="เจ้าของระบบ"><Loading /></AdminLayout>;

  const owners = data?.owners ?? [];
  const candidates = (data?.employees ?? []).filter((e) => e.role !== 'OWNER');

  async function promote() {
    if (!pick) return toast.error('เลือกพนักงานก่อน');
    const emp = candidates.find((c) => c.emp_code === pick);
    if (!window.confirm(`ตั้ง "${emp?.name ?? pick}" เป็นเจ้าของระบบ (Owner)?\nจะได้สิทธิ์สูงสุดทั้งหมด — ตั้งได้หลายคน`)) return;
    try {
      const r = await setRole.mutateAsync({ emp_code: pick, role: 'OWNER' });
      toast.success(r.applied ? 'ตั้งเป็นเจ้าของระบบแล้ว (มีผลทันที)' : 'บันทึกแล้ว — จะมีผลเมื่อผูก LINE');
      setPick('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ตั้งไม่สำเร็จ');
    }
  }

  async function demote(empCode: string, name: string) {
    if (!window.confirm(`ถอด "${name}" ออกจากการเป็นเจ้าของระบบ?\nจะเปลี่ยนเป็นสิทธิ์พนักงานทั่วไป`)) return;
    try {
      await setRole.mutateAsync({ emp_code: empCode, role: 'EMPLOYEE' });
      toast.success('ถอดออกจากเจ้าของระบบแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ถอดไม่สำเร็จ');
    }
  }

  return (
    <AdminLayout title="เจ้าของระบบ">
      <div className="space-y-4">
        <div className="card p-4">
          <div className="flex items-center gap-2 font-semibold mb-1">
            <Crown className="w-4 h-4 text-gold-500" /> เจ้าของระบบ (Owner)
          </div>
          <p className="text-xs text-gray-500">
            เจ้าของระบบมีสิทธิ์สูงสุด (เห็นเงินเดือน · ตั้งสิทธิ์คนอื่น · ปิดงวด) · ตั้งได้หลายคน ·
            <b> เฉพาะเจ้าของระบบเท่านั้นที่ตั้ง/ถอดเจ้าของได้ (HR ทำไม่ได้)</b>
          </p>
        </div>

        {/* รายชื่อเจ้าของปัจจุบัน */}
        <div className="card p-4 space-y-2">
          <div className="text-xs uppercase tracking-wider text-gray-500">เจ้าของระบบปัจจุบัน ({owners.length})</div>
          {owners.length === 0 && <p className="text-sm text-gray-500">ยังไม่มี</p>}
          {owners.map((o) => (
            <div key={o.emp_code} className="flex items-center justify-between gap-2 py-2 border-b border-gray-100 last:border-0">
              <div className="min-w-0">
                <p className="font-medium truncate flex items-center gap-1.5">
                  <Crown className="w-3.5 h-3.5 text-gold-500 shrink-0" /> {o.name}
                  {!o.line_paired && <span className="badge-warn shrink-0">ยังไม่ผูก LINE</span>}
                </p>
                <p className="text-xs text-gray-500">{o.emp_code}{o.department ? ` · ${o.department}` : ''}</p>
              </div>
              <button
                onClick={() => demote(o.emp_code, o.name)}
                disabled={setRole.isPending || owners.length <= 1}
                className="btn-secondary text-sm shrink-0"
                title={owners.length <= 1 ? 'ต้องมีเจ้าของอย่างน้อย 1 คน' : 'ถอดออกจากเจ้าของระบบ'}
              >
                <ShieldOff className="w-4 h-4" /> ถอด
              </button>
            </div>
          ))}
        </div>

        {/* ตั้งเจ้าของเพิ่ม */}
        <div className="card p-4 space-y-2">
          <div className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
            <UserPlus className="w-3.5 h-3.5" /> ตั้งพนักงานเป็นเจ้าของระบบเพิ่ม
          </div>
          <select className="input" value={pick} onChange={(e) => setPick(e.target.value)}>
            <option value="">— เลือกพนักงาน —</option>
            {candidates.map((c) => (
              <option key={c.emp_code} value={c.emp_code}>
                {c.name} ({c.emp_code}){c.department ? ` · ${c.department}` : ''}
              </option>
            ))}
          </select>
          <button onClick={promote} disabled={setRole.isPending || !pick} className="btn-cta w-full">
            <Crown className="w-4 h-4" /> ตั้งเป็นเจ้าของระบบ
          </button>
          <p className="text-[11px] text-gray-400">
            ถ้าพนักงานผูก LINE แล้ว สิทธิ์เปลี่ยนทันที · ยังไม่ผูก = มีผลตอนผูก LINE
          </p>
        </div>
      </div>
    </AdminLayout>
  );
}
